import type { SideMenuItemDto } from '@api-client';

import type { EnabledModule } from '../shared/services/modules/module-registry.models';
import { MODULE_ART, MODULE_FAMILIES } from './module-palette';

/**
 * A side-menu node as the launchpad reads it. `id` (TenantConfig MenuItemReference.Id) and `isFavorite`
 * come with ZEY-2664; both stay optional until the generated client carries them, so an API without
 * them simply means no favorites.
 */
export type LaunchpadMenuItem = SideMenuItemDto & { id?: number; isFavorite?: boolean };

/** Key of the group that lists the user's favorites, above the families (ZEY-2665). */
export const FAVORITES_GROUP = 'favorites';

export interface LaunchpadTile {
  /** Module.Key — the tile opens /app/{key}. */
  key: string;
  /** The menu item's id, which a favorite is saved against. No id, no star. */
  menuItemId?: number;
  /** Top-level menu uid; also the translation key of the module name. */
  uid: string;
  /** Kendo icon class from the menu, used when the module has no art of its own. */
  icon?: string;
  /** assets/icons/modules/{key}-tile.svg exists. */
  hasArt: boolean;
}

export interface LaunchpadGroup {
  /** Family key ('finance', …), 'other' for modules without art, or FAVORITES_GROUP. */
  key: string;
  tiles: LaunchpadTile[];
}

const byOrder = (a: SideMenuItemDto, b: SideMenuItemDto) => (a.insertionOrder ?? 0) - (b.insertionOrder ?? 0);

/**
 * menu uid (lower case) -> module key. The API's menuUid (?includeNative=true) wins; the palette's
 * static copy covers an API that doesn't send it yet.
 */
export function moduleKeysByMenuUid(modules: EnabledModule[]): Map<string, string> {
  const keyByUid = new Map<string, string>();
  for (const [key, art] of Object.entries(MODULE_ART)) keyByUid.set(art.menuUid.toLowerCase(), key);
  for (const m of modules) {
    if (m.menuUid) keyByUid.set(m.menuUid.toLowerCase(), m.key);
  }
  return keyByUid;
}

/** The menu uid of a module key, from the API when it sends one, else from the palette. */
export function menuUidForKey(key: string, modules: EnabledModule[]): string | undefined {
  return modules.find(m => m.key === key)?.menuUid ?? MODULE_ART[key]?.menuUid;
}

/**
 * One tile per top-level menu node the user can see (the menu is already filtered by role and plan),
 * matched to its module through its menu uid. A node with no module is skipped: without a key there is
 * no /app/{key} to open.
 */
export function buildLaunchpadTiles(menu: LaunchpadMenuItem[], modules: EnabledModule[]): LaunchpadTile[] {
  const keyByUid = moduleKeysByMenuUid(modules);

  const tiles: LaunchpadTile[] = [];
  for (const node of [...menu].sort(byOrder)) {
    const key = node.uid ? keyByUid.get(node.uid.toLowerCase()) : undefined;
    if (!key) {
      console.warn(`Launchpad: menu item '${node.uid}' has no module; no tile.`);
      continue;
    }
    tiles.push({ key, uid: node.uid, icon: node.icon, hasArt: !!MODULE_ART[key], menuItemId: node.id ?? undefined });
  }
  return tiles;
}

/** The menu items the user starred. Launchpad v1 stars modules only, so only top-level nodes count. */
export function favoriteMenuItemIds(menu: LaunchpadMenuItem[]): Set<number> {
  const ids = new Set<number>();
  for (const node of menu) {
    if (node.isFavorite && node.id != null) ids.add(node.id);
  }
  return ids;
}

/**
 * The user's favorites first, in menu order (the order the tiles come in), when there is at least one.
 * Then tiles in family order (the design's groups), then any module without art under 'other'.
 * A favorite also stays in its own family.
 */
export function groupLaunchpadTiles(tiles: LaunchpadTile[], favoriteIds: ReadonlySet<number> = new Set()): LaunchpadGroup[] {
  const groups: LaunchpadGroup[] = [];
  const favorites = tiles.filter(t => t.menuItemId != null && favoriteIds.has(t.menuItemId));
  if (favorites.length) groups.push({ key: FAVORITES_GROUP, tiles: favorites });
  for (const family of MODULE_FAMILIES) {
    const inFamily = family.modules
      .map(key => tiles.find(t => t.key === key))
      .filter((t): t is LaunchpadTile => !!t);
    if (inFamily.length) groups.push({ key: family.key, tiles: inFamily });
  }
  const other = tiles.filter(t => !t.hasArt);
  if (other.length) groups.push({ key: 'other', tiles: other });
  return groups;
}

/** Where a native module opens: its own path, else its first child with a path, in menu order. */
export function firstModulePath(node: SideMenuItemDto): string | undefined {
  if (node.path) return node.path;
  return [...(node.children ?? [])].sort(byOrder).find(c => !!c.path)?.path;
}
